import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import useApi from "./useApi";
import {
  decideWorkflowRunApproval,
  loadWorkflowStageApprovals,
  WorkflowRunDecideResult,
} from "../services/workflowService";
import {
  WorkflowApprovalDecideRequestDto,
  WorkflowApprovalDecisionDto,
  WorkflowApprovalStageGroupDto,
} from "../types/workflowDto";

export type WorkflowApprovalDecision = Exclude<WorkflowApprovalDecisionDto, "pending">;

export type WorkflowApprovalOutcome =
  /** Recorded. `stageResolved` false = the counting model still waits on other approvers.
   *  `group` is the stage as re-read straight after (null if that re-read failed). */
  | { kind: "decided"; stageResolved: boolean; group: WorkflowApprovalStageGroupDto | null }
  | { kind: "conflict" }
  | { kind: "not-permitted" }
  | { kind: "unresolved" }
  | { kind: "not-found" }
  /** No answer from the server — useApi has raised its alert; the same click may be retried. */
  | { kind: "failed" };

export interface WorkflowApprovalTarget {
  instanceId: number;
  stageId: number;
  rowVersionTag: string;
}

export type WorkflowApprovalsLoadStatus = "idle" | "loading" | "loaded" | "error";

/**
 * A real case's approval stage: its approvers and their decisions (GET approvals), and the
 * decision itself (approval/decide).
 *
 * An approval must be exactly one stamped event, so: a second click while a decision is in flight
 * is ignored, and the idempotency key is kept until the server gives a definite answer — a
 * decision retried after a dropped connection (or a 401 replay) is the same decision, and the
 * server answers it as a replay rather than stamping twice.
 */
const useWorkflowApprovalStage = (target: WorkflowApprovalTarget | null) => {
  const { executeApi } = useApi();
  const instanceId = target?.instanceId ?? null;
  const stageId = target?.stageId ?? null;
  const loadedTag = target?.rowVersionTag ?? "";

  const [group, setGroup] = useState<WorkflowApprovalStageGroupDto | null>(null);
  const [loadStatus, setLoadStatus] = useState<WorkflowApprovalsLoadStatus>(target ? "loading" : "idle");
  const [attempt, setAttempt] = useState(0);
  const [deciding, setDeciding] = useState(false);
  const decidingRef = useRef(false);
  const keyRef = useRef<string | null>(null);

  useEffect(() => {
    keyRef.current = null;
  }, [instanceId, stageId, loadedTag]);

  useEffect(() => {
    if (!instanceId || !stageId) {
      setGroup(null);
      setLoadStatus("idle");
      return undefined;
    }
    let active = true;
    setLoadStatus("loading");
    executeApi<{ instanceId: number; stageId: number }, { group: WorkflowApprovalStageGroupDto | null }>(
      loadWorkflowStageApprovals,
      { instanceId, stageId },
    ).then((result) => {
      if (!active) return;
      // null = the read failed (offer Retry); { group: null } = no approver was resolved.
      if (!result) {
        setLoadStatus("error");
        return;
      }
      setGroup(result.group);
      setLoadStatus("loaded");
    });
    return () => {
      active = false;
    };
  }, [executeApi, instanceId, stageId, attempt]);

  const reload = useCallback(() => setAttempt((current) => current + 1), []);

  const decide = useCallback(
    async (decision: WorkflowApprovalDecision, reason: string): Promise<WorkflowApprovalOutcome | null> => {
      if (!instanceId || !stageId || decidingRef.current) return null;
      decidingRef.current = true;
      setDeciding(true);
      keyRef.current ??= uuidv4();
      try {
        const result = await executeApi<WorkflowApprovalDecideRequestDto, WorkflowRunDecideResult>(
          decideWorkflowRunApproval,
          {
            instanceId,
            stageId,
            // 0 = let the server resolve the caller's own pending row on this stage.
            approvalId: 0,
            decision,
            reason,
            rowVersionTag: loadedTag,
            idempotencyKey: keyRef.current,
          },
        );
        if (!result) return { kind: "failed" };
        keyRef.current = null;
        if (result.kind !== "decided") return { kind: result.kind };
        // Re-read the stage so the new stamp (name + DecidedOn, both server-set) shows at once.
        const refreshed = await executeApi<
          { instanceId: number; stageId: number },
          { group: WorkflowApprovalStageGroupDto | null }
        >(loadWorkflowStageApprovals, { instanceId, stageId });
        if (refreshed) setGroup(refreshed.group);
        return { kind: "decided", stageResolved: result.result.stageResolved, group: refreshed?.group ?? null };
      } finally {
        decidingRef.current = false;
        setDeciding(false);
      }
    },
    [executeApi, instanceId, stageId, loadedTag],
  );

  return { group, loadStatus, reload, deciding, decide };
};

export default useWorkflowApprovalStage;
